import { Box, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomFormControl from '@/components/inputs/CustomFormControl'
import MainContentContainer from '@/components/layouts/MainContentContainer'
import Page from '@/components/layouts/Page'
import { useShellFullWidth } from '@/components/layouts/Shell'
import BidOfferCascadingContent from '@/features/bidding/components/cascading/BidOfferCascadingContent'
import BidOfferCascadingSkeleton from '@/features/bidding/components/cascading/BidOfferCascadingSkeleton'
import MarketTimelineSelector from '@/features/bidding/components/cascading/market-timeline/MarketTimelineSelector'
import { useMarketTimelineState } from '@/features/bidding/components/cascading/market-timeline/useMarketTimelineState'
import MarketGateClosed from '@/features/bidding/components/cascading/MarketGateClosed'
import { MARKET_TIMEZONE } from '@/features/bidding/constants'
import { useBidOfferCascadingData } from '@/features/bidding/hooks/useBidOfferCascadingData'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

// Default bid status used while data is loading (required for useMarketTimelineState hook)
const DEFAULT_BID_STATUS: Record<CascadeMarketId, boolean> = {
  mfrr: false,
  'da-wholesale': false,
  'fcr-d': false,
  'fcr-n': false,
}

function getDisabledCascadeMarkets({
  isFcrdEnabled,
  isFcrnEnabled,
  isMfrrEnabled,
}: {
  isFcrdEnabled: boolean
  isFcrnEnabled: boolean
  isMfrrEnabled: boolean
}): CascadeMarketId[] {
  const disabled: CascadeMarketId[] = ['da-wholesale']
  if (!isMfrrEnabled) disabled.push('mfrr')
  if (!isFcrdEnabled) disabled.push('fcr-d')
  if (!isFcrnEnabled) disabled.push('fcr-n')
  return disabled
}

function BidOfferCascading() {
  const { i18n, t } = useTranslation()
  const { isEnabled } = useFeatureToggle()
  const { setIsFullWidth } = useShellFullWidth()
  const navigate = useNavigate()

  const [selectedDeliveryDay, setSelectedDeliveryDay] = useState<DateTime>(
    DateTime.now().setZone(MARKET_TIMEZONE).plus({ days: 2 }),
  )
  const [selectedMarket, setSelectedMarket] = useState<CascadeMarketId>('mfrr')

  const isMfrrEnabled = isEnabled('FLEX_PORTAL_BID_CASCADING_MFRR_ENABLED')
  const isFcrdEnabled = isEnabled('FLEX_PORTAL_BID_CASCADING_FCRD_ENABLED')
  const isFcrnEnabled = isEnabled('FLEX_PORTAL_BID_CASCADING_FCRN_ENABLED')

  const disabledMarkets = getDisabledCascadeMarkets({ isFcrdEnabled, isFcrnEnabled, isMfrrEnabled })

  // Check if the currently selected market is disabled
  const isSelectedMarketDisabled = disabledMarkets.includes(selectedMarket)

  // Memoize the delivery day MarketDate to avoid recreating on each render
  const deliveryDay = useMemo(() => new MarketDate(selectedDeliveryDay.toFormat('yyyy-MM-dd')), [selectedDeliveryDay])

  const cascadingDataResult = useBidOfferCascadingData({
    deliveryDay,
    selectedMarket,
  })

  // Use default bid status while loading (required for useMarketTimelineState hook)
  const bidStatus = cascadingDataResult.data?.bidStatus ?? DEFAULT_BID_STATUS

  // Get market timeline state to check if market gate is closed
  const { markets: marketStates } = useMarketTimelineState({
    selectedDate: selectedDeliveryDay,
    bidStatus,
  })

  // Find the current market state and check if it's closed
  const currentMarketState = useMemo(
    () => marketStates.find((m) => m.id === selectedMarket),
    [marketStates, selectedMarket],
  )
  const isMarketGateClosed = currentMarketState?.state === 'closed'

  // Enable full-width mode for this page
  useEffect(() => {
    setIsFullWidth(true)
    return () => setIsFullWidth(false)
  }, [setIsFullWidth])

  // Navigate to bidding overview after successful bid creation, preserving country and delivery day
  const handleBidCreationSuccess = useCallback(() => {
    const params = new URLSearchParams({
      'globalFilter.countryCode': 'fi',
      'filter.deliveryDay.equals': selectedDeliveryDay.toISODate()!,
    })
    navigate(`/bidding?${params.toString()}`)
  }, [navigate, selectedDeliveryDay])

  const handleDateChange = (date: DateTime | null) => {
    if (date) {
      setSelectedDeliveryDay(date)
    }
  }

  // Key for the content component - changes trigger a full remount, resetting all internal state
  const contentKey = `${selectedMarket}-${selectedDeliveryDay.toISODate()}`

  // Render content based on loading state
  const renderContent = () => {
    // Show message when selected market is disabled
    if (isSelectedMarketDisabled) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Typography color="text.secondary" variant="h6">
            {t('bidding.bidding_cascading.select_market_message')}
          </Typography>
        </Box>
      )
    }

    if (cascadingDataResult.isLoading) {
      return <BidOfferCascadingSkeleton isCollapsed={false} />
    }

    // After loading check, data is guaranteed to be defined
    const data = cascadingDataResult.data

    if (isMarketGateClosed) {
      return <MarketGateClosed deliveryDay={selectedDeliveryDay} marketLabel={currentMarketState?.label ?? ''} />
    }

    return (
      <BidOfferCascadingContent
        key={contentKey}
        cascadingData={data}
        deliveryDay={deliveryDay}
        selectedDeliveryDay={selectedDeliveryDay}
        selectedMarket={selectedMarket}
        onBidCreationSuccess={handleBidCreationSuccess}
      />
    )
  }

  return (
    <Page
      pageHeaderProps={{
        pageTitle: t('bidding.bidding_cascading.page_title'),
        isRoot: true,
        appBarContent: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
              <CustomFormControl variant="outlinedWhite">
                <DatePicker
                  enableAccessibleFieldDOMStructure={false}
                  format="DD"
                  label={t('bidding.bidding_cascading.date_picker_label')}
                  minDate={DateTime.now().setZone(MARKET_TIMEZONE).plus({ days: 1 }).startOf('day')}
                  sx={{ width: 180 }}
                  value={selectedDeliveryDay}
                  onChange={handleDateChange}
                />
              </CustomFormControl>
            </LocalizationProvider>
            <MarketTimelineSelector
              bidStatus={bidStatus}
              disabledMarkets={disabledMarkets}
              selectedDate={selectedDeliveryDay}
              selectedMarket={selectedMarket}
              onSelectMarket={setSelectedMarket}
            />
          </Box>
        ),
      }}
    >
      <MainContentContainer
        sx={{
          p: 0,
          // Calculate height: 100vh minus Shell toolbar (64px), Container margin (16px),
          // PageTitle (~40px), and PageAppBar with margin (~80px)
          height: 'calc(100vh - 216px)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', height: '100%' }}>{renderContent()}</Box>
      </MainContentContainer>
    </Page>
  )
}

export default BidOfferCascading
